import { Inject, Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { CommonEventLog } from '../../models/common-event-log';
import { OrderStateEventLog } from '../../models/order-state-event-log';
import { SupplierWorktimeEventLog } from '../../models/supplier-worktime-event-log';
import { SupplierResourceEventLog } from '../../models/supplier-resource-event-log';
import { ErrorLogDto } from '../../dtos/error-log-dto';
import { InfoLogDto } from '../../dtos/info-log-dto';
import { ILoggerClientService } from './ilogger-client.service';
import { AppSettings } from '../../../appsettings';
import {
    IAuthenticationService,
    AUTH_SERVICE_IMPL,
} from '../authentication/iauthentication.service';
import { ServiceBaseService } from '../service-base.service';
import { ModuleID } from '../../types/module-id';
import { EnvironmentName } from '../../types/environment-name';
import { EventType } from '../../types/event-type';
import { ActionType } from '../../types/action-type';
import { ActorType } from '../../types/actor-type';
import { CustomLogger } from '../../types/custom-logger';
import {
    LegacyActionType,
    LegacyActorType,
    LegacyCustomLogger,
    LegacyEnvironmentName,
    LegacyEventType,
    LegacyModuleID,
} from '../../types/legacy-logger-enums';

@Injectable()
export class LoggerClientService implements ILoggerClientService {
    constructor(
        private _httpClient: HttpClient,
        @Inject(AUTH_SERVICE_IMPL) private _authService: IAuthenticationService
    ) {}

    public addCommonEventLog(
        commonEventLog: CommonEventLog
    ): Observable<HttpResponse<boolean>> {
        var environmentName = ServiceBaseService.getEnvironmentName;
        commonEventLog.environmentName = environmentName;

        var actor = this._authService.currentUser;
        commonEventLog.actorType = actor.actorType;
        commonEventLog.userName = actor.actorUserName;
        commonEventLog.ip = actor.actorIp;
        commonEventLog.userAgent = actor.actorUserAgent;

        return this.postToLogServices('event/common', commonEventLog);
    }

    public addOrderStateEventLog(
        orderStateEventLog: OrderStateEventLog
    ): Observable<HttpResponse<boolean>> {
        var environmentName = ServiceBaseService.getEnvironmentName;

        orderStateEventLog.environmentName = environmentName;

        var user = this._authService.getUserFromStorage();

        orderStateEventLog.actorType = user.actorType;
        orderStateEventLog.userName = user.actorUserName;
        orderStateEventLog.ip = user.actorIp;
        orderStateEventLog.userAgent = user.actorUserAgent;

        return this.postToLogServices('event/order-state', orderStateEventLog);
    }

    public addSupplierWorktimeEventLog(
        supplierWorktimeEventLog: SupplierWorktimeEventLog
    ): Observable<HttpResponse<boolean>> {
        var environmentName = ServiceBaseService.getEnvironmentName;
        supplierWorktimeEventLog.environmentName = environmentName;

        var actor = this._authService.currentUser;
        supplierWorktimeEventLog.actorType = actor.actorType;
        supplierWorktimeEventLog.userName = actor.actorUserName;
        supplierWorktimeEventLog.ip = actor.actorIp;
        supplierWorktimeEventLog.userAgent = actor.actorUserAgent;

        return this.postToLogServices(
            'event/supplier-worktime',
            supplierWorktimeEventLog
        );
    }

    public addSupplierResourceEventLog(
        supplierResourceEventLog: SupplierResourceEventLog
    ): Observable<HttpResponse<boolean>> {
        var environmentName = ServiceBaseService.getEnvironmentName;
        supplierResourceEventLog.environmentName = environmentName;

        var actor = this._authService.currentUser;
        supplierResourceEventLog.actorType = actor.actorType;
        supplierResourceEventLog.userName = actor.actorUserName;
        supplierResourceEventLog.ip = actor.actorIp;
        supplierResourceEventLog.userAgent = actor.actorUserAgent;

        return this.postToLogServices(
            'event/supplier-resource',
            supplierResourceEventLog
        );
    }

    public addErrorLog(
        errorLogDto: ErrorLogDto,
        moduleID: ModuleID
    ): Observable<HttpResponse<boolean>> {
        var environmentName = ServiceBaseService.getEnvironmentName;
        errorLogDto.environmentName = environmentName;

        errorLogDto.moduleID = moduleID;

        return this.postToLogServices('error', errorLogDto);
    }

    public addInfoLog(
        infoLogDto: InfoLogDto,
        moduleID: ModuleID
    ): Observable<HttpResponse<boolean>> {
        var environmentName = ServiceBaseService.getEnvironmentName;
        infoLogDto.environmentName = environmentName;

        infoLogDto.moduleID = moduleID;

        return this.postToLogServices('info', infoLogDto);
    }

    private postToLogServices<T extends object>(
        endpoint: string,
        dto: T
    ): Observable<HttpResponse<boolean>> {
        const newLoggerRequest = this.postSafely(
            AppSettings.get('loggerBaseURL'),
            endpoint,
            dto
        );
        const legacyLoggerBaseUrl = AppSettings.get('legacyLoggerBaseURL');

        if (!legacyLoggerBaseUrl) {
            return newLoggerRequest;
        }

        const legacyDto = this.mapToLegacyDto(dto);
        const legacyLoggerRequest = this.postSafely(
            legacyLoggerBaseUrl,
            endpoint,
            legacyDto
        );

        return forkJoin([newLoggerRequest, legacyLoggerRequest]).pipe(
            map(([newLoggerResponse]) => newLoggerResponse)
        );
    }

    private post<T>(
        loggerBaseUrl: string,
        endpoint: string,
        dto: T
    ): Observable<HttpResponse<boolean>> {
        return this._httpClient.post<boolean>(`${loggerBaseUrl}/${endpoint}`, dto, {
            observe: 'response',
            withCredentials: true,
        });
    }

    private postSafely<T>(
        loggerBaseUrl: string,
        endpoint: string,
        dto: T
    ): Observable<HttpResponse<boolean>> {
        return this.post(loggerBaseUrl, endpoint, dto).pipe(
            catchError((error) => {
                // Logging must never interrupt the user action that triggered it.
                console.error(`Log request failed: ${loggerBaseUrl}/${endpoint}`, error);

                return of(
                    new HttpResponse<boolean>({
                        body: false,
                        status: 0,
                        statusText: 'Log request failed',
                    })
                );
            })
        );
    }

    private mapToLegacyDto<T extends object>(dto: T): T {
        const legacyDto = { ...dto };

        // ErrorID only exists in the new error-log contract.
        delete legacyDto['errorID'];

        this.mapLegacyEnumProperty(
            legacyDto,
            'environmentName',
            EnvironmentName,
            LegacyEnvironmentName,
            LegacyEnvironmentName.NONE_OF_THEM
        );
        this.mapLegacyEnumProperty(legacyDto, 'moduleID', ModuleID, LegacyModuleID);
        this.mapLegacyEnumProperty(legacyDto, 'eventType', EventType, LegacyEventType);
        this.mapLegacyEnumProperty(legacyDto, 'actionType', ActionType, LegacyActionType);
        this.mapLegacyEnumProperty(legacyDto, 'actorType', ActorType, LegacyActorType);
        this.mapLegacyEnumProperty(
            legacyDto,
            'customLogger',
            CustomLogger,
            LegacyCustomLogger
        );

        return legacyDto;
    }

    private mapLegacyEnumProperty(
        dto: object,
        propertyName: string,
        currentEnum: object,
        legacyEnum: object,
        unsupportedValue?: number
    ): void {
        const currentValue = dto[propertyName];

        if (currentValue === undefined || currentValue === null) {
            return;
        }

        const enumName = currentEnum[currentValue];
        const legacyValue = legacyEnum[enumName];

        if (typeof legacyValue === 'number') {
            dto[propertyName] = legacyValue;
            return;
        }

        if (unsupportedValue !== undefined) {
            dto[propertyName] = unsupportedValue;
            return;
        }

        throw new Error(
            `${propertyName} value ${currentValue} has no legacy logger equivalent.`
        );
    }
}
